<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { onMounted, onUnmounted } from 'vue';
import ResponsiveImage from '@/components/public/ResponsiveImage.vue';
import { useScrollReveal } from '@/composables/useScrollReveal';
import PublicLayout from '@/layouts/PublicLayout.vue';

const revealHistoire = useScrollReveal({ rootMargin: '0px 0px -40px 0px' });
const revealTable = useScrollReveal({ rootMargin: '0px 0px -40px 0px' });
const revealCuisine = useScrollReveal({ rootMargin: '0px 0px -40px 0px' });
const revealChambres = useScrollReveal({ rootMargin: '0px 0px -40px 0px' });

onMounted(() => {
    document.documentElement.style.scrollSnapType = 'y mandatory';
    document.documentElement.style.overflowY = 'scroll';
});
onUnmounted(() => {
    document.documentElement.style.scrollSnapType = '';
    document.documentElement.style.overflowY = '';
});
</script>

<template>
    <PublicLayout>
        <!-- HERO — pleine page avec logo -->
        <section
            class="relative h-screen min-h-[600px] [scroll-snap-align:start] [scroll-snap-stop:always] overflow-hidden"
        >
            <!-- Fond en <img> et non en background-image CSS : un fond CSS n'est
                 découvert qu'après le parsing de la feuille de style, donc trop tard. -->
            <ResponsiveImage
                src="/images/background.webp"
                alt=""
                aria-hidden="true"
                sizes="100vw"
                eager
                class="absolute inset-0 h-full w-full object-cover object-center"
            />
            <ResponsiveImage
                src="/images/home.webp"
                alt="Plat signature du chef Laurent Bouchend'homme, Auberge La Péniche"
                sizes="100vw"
                priority
                class="absolute inset-0 h-full w-full object-cover object-center"
                style="opacity: 0.47"
            />
            <div
                class="absolute inset-0 bg-gradient-to-b from-[rgba(10,10,8,0.25)] to-[rgba(10,10,8,0.65)]"
            />
            <div
                class="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
            >
                <p class="eyebrow mb-9 font-heading">Pierrefitte-sur-Loire</p>
                <h1
                    class="mb-8 flex flex-col items-center justify-center gap-2 font-display text-[clamp(5rem,16vw,12rem)] leading-[0.9] font-bold tracking-[0.04em] text-white uppercase sm:flex-row sm:items-center sm:gap-[0.08em]"
                >
                    <img
                        src="/images/la.webp"
                        alt="La"
                        width="334"
                        height="415"
                        decoding="async"
                        class="block h-[1em] w-auto sm:h-[1.5em] sm:-translate-y-[0.24em]"
                    />
                    <span>Péniche</span>
                </h1>
                <p
                    class="font-heading text-[1.7rem] font-light tracking-[0.3em] text-peniche-cream uppercase"
                >
                    Refuge Gourmand
                </p>
            </div>
            <!-- Scroll indicator -->
            <div
                class="absolute bottom-9 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1.5"
            >
                <div
                    class="h-12 w-px bg-gradient-to-b from-transparent to-[rgba(255,255,255,0.5)]"
                />
                <div
                    class="h-2 w-2 rotate-45 border-r border-b border-[rgba(255,255,255,0.5)]"
                />
            </div>
        </section>

        <!-- L'HISTOIRE -->
        <section
            class="flex min-h-screen [scroll-snap-align:start] [scroll-snap-stop:always] overflow-hidden"
        >
            <!-- 2/3 texte -->
            <div
                class="flex flex-[0_0_66.667%] items-center bg-peniche-dark px-[8%] py-20"
            >
                <div
                    :ref="revealHistoire.el"
                    class="reveal-up max-w-[560px]"
                    :class="{ 'is-visible': revealHistoire.visible.value }"
                >
                    <div class="mb-10 flex items-center gap-4">
                        <div class="divider-gold-thin" />
                        <span
                            class="text-[0.55rem] font-semibold tracking-[0.35em] text-brand uppercase"
                            >L'Auberge La Péniche</span
                        >
                    </div>
                    <p
                        class="mb-4 font-display text-[clamp(1.2rem,2vw,1.65rem)] leading-[1.7] font-normal text-white italic"
                    >
                        L'Auberge La Péniche, un havre de convivialité et
                        d'authenticité niché entre l'Allier et la Loire, aux
                        portes de l'Auvergne-Rhône-Alpes.
                    </p>
                    <p
                        class="mb-10 font-display text-[clamp(1.2rem,2vw,1.65rem)] leading-[1.7] font-normal text-peniche-cream italic"
                    >
                        Ici, le temps semble ralentir. La nature est proche,
                        généreuse et l'accueil se veut simple et sincère, comme
                        une invitation à faire une pause, loin de l'agitation du
                        quotidien.
                    </p>
                    <div class="divider-gold-thin mb-12" />
                    <Link href="/notre-histoire" class="btn-outline-gold"
                        >Notre histoire</Link
                    >
                </div>
            </div>
            <!-- 1/3 image -->
            <div class="relative flex-[0_0_33.333%] overflow-hidden">
                <!-- Section immédiatement suivante : chargée sans attendre le scroll, car
                     le scroll-snap saute une section entière d'un coup. Sans `priority`,
                     elle se met en file derrière l'image LCP. -->
                <ResponsiveImage
                    src="/images/section-2.webp"
                    alt="L'Auberge La Péniche"
                    sizes="33vw"
                    eager
                    class="absolute inset-0 h-full w-full object-cover brightness-[0.8] saturate-[0.85]"
                />
                <svg class="absolute h-0 w-0 overflow-hidden">
                    <filter
                        id="grain-section2"
                        x="0%"
                        y="0%"
                        width="100%"
                        height="100%"
                        color-interpolation-filters="sRGB"
                    >
                        <feTurbulence
                            type="turbulence"
                            baseFrequency="0.04 0.06"
                            numOctaves="6"
                            seed="7"
                            stitchTiles="stitch"
                        />
                        <feColorMatrix type="saturate" values="0" />
                    </filter>
                </svg>
                <div
                    class="pointer-events-none absolute inset-0 opacity-[0.22] mix-blend-overlay"
                    style="filter: url(#grain-section2)"
                />
                <div
                    class="pointer-events-none absolute inset-0 bg-gradient-to-r from-peniche-dark to-transparent"
                />
            </div>
        </section>

        <!-- LA TABLE — bannière image -->
        <section
            class="relative h-screen min-h-[600px] [scroll-snap-align:start] [scroll-snap-stop:always] overflow-hidden"
        >
            <ResponsiveImage
                src="/images/background.webp"
                alt="La Table — La Péniche"
                sizes="100vw"
                class="absolute inset-0 h-full w-full object-cover object-center"
            />
            <ResponsiveImage
                src="/images/home-table.webp"
                alt=""
                aria-hidden="true"
                sizes="100vw"
                class="absolute inset-0 h-full w-full object-cover object-center"
                style="opacity: 0.47"
            />
            <div
                class="absolute inset-0 bg-gradient-to-r from-[rgba(10,10,8,0.6)] to-[rgba(10,10,8,0.15)]"
            />
            <div
                :ref="revealTable.el"
                class="reveal-up relative z-10 flex h-full flex-col items-start justify-center px-[8%]"
                :class="{ 'is-visible': revealTable.visible.value }"
            >
                <h2
                    class="mb-5 font-display text-[clamp(3.5rem,10vw,8rem)] leading-[0.9] font-bold tracking-[0.04em] text-white uppercase"
                >
                    La Table
                </h2>
                <p
                    class="mb-10 font-heading text-[1.1rem] font-light tracking-[0.1em] text-peniche-cream italic"
                >
                    Découvrez nos menus
                </p>
                <Link href="/la-table" class="btn-gold">Voir la carte</Link>
            </div>
        </section>

        <!-- LA CUISINE -->
        <section
            class="flex min-h-screen [scroll-snap-align:start] [scroll-snap-stop:always] overflow-hidden"
        >
            <!-- 1/3 image -->
            <div class="relative flex-[0_0_33.333%] overflow-hidden">
                <ResponsiveImage
                    src="/images/background.webp"
                    alt="La cuisine de La Péniche"
                    sizes="33vw"
                    class="absolute inset-0 h-full w-full object-cover object-center"
                />
                <ResponsiveImage
                    src="/images/home-cuisine.webp"
                    alt=""
                    aria-hidden="true"
                    sizes="33vw"
                    class="absolute inset-0 h-full w-full object-cover object-center"
                    style="opacity: 0.47"
                />
                <div
                    class="pointer-events-none absolute inset-0 bg-gradient-to-l from-peniche-dark to-transparent"
                />
            </div>
            <!-- 2/3 texte -->
            <div
                class="flex flex-[0_0_66.667%] items-center bg-peniche-dark px-[8%] py-20"
            >
                <div
                    :ref="revealCuisine.el"
                    class="reveal-up max-w-[560px]"
                    :class="{ 'is-visible': revealCuisine.visible.value }"
                >
                    <div class="mb-10 flex items-center gap-4">
                        <div class="divider-gold-thin" />
                        <span
                            class="text-[0.55rem] font-semibold tracking-[0.35em] text-brand uppercase"
                            >La Cuisine</span
                        >
                    </div>
                    <p
                        class="mb-10 font-display text-[clamp(1.2rem,2vw,1.65rem)] leading-[1.7] font-normal text-white italic"
                    >
                        Une cuisine sincère, vivante et généreuse, en accord
                        avec le territoire qui nous entoure.
                    </p>
                    <div class="divider-gold-thin mb-10" />
                    <p
                        class="m-0 font-display text-[clamp(0.95rem,1.4vw,1.1rem)] leading-[1.9] font-normal text-peniche-subtle"
                    >
                        Notre chef Laurent Bouchend'homme s'inspire de la nature
                        environnante, des saisons, des produits locaux et des
                        cueillettes sauvages. Le menu, unique et vivant, se
                        transforme au fil des récoltes.
                    </p>
                </div>
            </div>
        </section>

        <!-- LES CHAMBRES -->
        <section
            class="relative h-screen min-h-[600px] [scroll-snap-align:start] [scroll-snap-stop:always] overflow-hidden"
        >
            <ResponsiveImage
                src="/images/background.webp"
                alt="Les Chambres — La Péniche"
                sizes="100vw"
                class="absolute inset-0 h-full w-full object-cover object-center"
            />
            <ResponsiveImage
                src="/images/home-chambre.webp"
                alt=""
                aria-hidden="true"
                sizes="100vw"
                class="absolute inset-0 h-full w-full object-cover object-center"
                style="opacity: 0.47"
            />
            <div
                class="absolute inset-0 bg-gradient-to-l from-[rgba(10,10,8,0.55)] to-[rgba(10,10,8,0.1)]"
            />
            <div
                :ref="revealChambres.el"
                class="reveal-up relative z-10 flex h-full flex-col items-end justify-center px-[8%] text-right"
                :class="{ 'is-visible': revealChambres.visible.value }"
            >
                <h2
                    class="mb-5 font-display text-[clamp(3.5rem,10vw,8rem)] leading-[0.9] font-bold tracking-[0.04em] text-white uppercase"
                >
                    Les Chambres
                </h2>
                <p
                    class="mb-10 font-heading text-[1.1rem] font-light tracking-[0.1em] text-peniche-cream italic"
                >
                    Ici, vous n'êtes pas des clients… mais des amis
                </p>
                <Link href="/les-chambres" class="btn-gold">Découvrir</Link>
            </div>
        </section>

        <!-- PHILOSOPHIE — Le lieu -->
        <section
            class="flex min-h-screen [scroll-snap-align:start] [scroll-snap-stop:always] overflow-hidden"
        >
            <!-- 2/3 texte -->
            <div
                class="flex flex-[0_0_66.667%] items-center bg-peniche-dark px-[8%] py-20"
            >
                <div class="max-w-[560px]">
                    <div class="mb-10 flex items-center gap-4">
                        <div class="divider-gold-thin" />
                        <span
                            class="text-[0.55rem] font-semibold tracking-[0.35em] text-brand uppercase"
                            >Un lieu pour se retrouver</span
                        >
                    </div>
                    <p
                        class="mb-10 font-display text-[clamp(1.2rem,2vw,1.65rem)] leading-[1.7] font-normal text-white italic"
                    >
                        Que vous voyagiez en solitaire, en couple, en famille ou
                        entre amis, vous êtes ici accueillis loin de l'agitation
                        de la ville, dans un cadre propice au repos, au partage
                        et à la simplicité.
                    </p>
                    <div class="divider-gold-thin mb-10" />
                    <p
                        class="mb-12 font-display text-[clamp(0.95rem,1.4vw,1.1rem)] leading-[1.9] text-peniche-muted2 italic"
                    >
                        Alexandra, Laurent et leur équipe sont heureux de vous
                        accueillir et vous invitent à retrouver l'essentiel sous
                        le signe de l'authenticité.
                    </p>
                    <Link href="/notre-histoire" class="btn-outline-gold"
                        >Notre histoire</Link
                    >
                </div>
            </div>
            <!-- 1/3 image -->
            <div class="relative flex-[0_0_33.333%] overflow-hidden">
                <ResponsiveImage
                    src="/images/background.webp"
                    alt=""
                    aria-hidden="true"
                    sizes="33vw"
                    class="absolute inset-0 h-full w-full object-cover object-center"
                />
                <ResponsiveImage
                    src="/images/home-retrouver.webp"
                    alt=""
                    aria-hidden="true"
                    sizes="33vw"
                    class="absolute inset-0 h-full w-full object-cover object-center"
                    style="opacity: 0.47"
                />
                <div
                    class="pointer-events-none absolute inset-0 bg-gradient-to-r from-peniche-dark to-transparent"
                />
            </div>
        </section>
    </PublicLayout>
</template>
